import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import {
  TextArea,
  Checkbox,
  NumericTextBox,
  Input,
} from "@progress/kendo-react-inputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";

/** Key to repeat last row value into current field. */
export const BATCH_POSTING_REPEAT_KEY = "Insert";

/** Returns true when the event is the repeat-from-previous shortcut (Insert key). */
export const isBatchPostingRepeatKey = (e) => e.key === BATCH_POSTING_REPEAT_KEY;

/**
 * Blur then refocus so Kendo inputs (e.g. NumericTextBox, DatePicker) sync with
 * the updated value prop and show it immediately instead of only after blur.
 */
export const blurThenRefocusForValueSync = () => {
  const el = document.activeElement;
  if (!el || typeof el.blur !== "function") return;
  el.blur();
  setTimeout(() => el.focus?.(), 20);
};

const CLabel = ({ label }) => {
  return <Label className="label">{label}</Label>;
};

export const validateCounselFee = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, ...others } =
    fieldRenderProps;
  return (
    <div className="d-flex">
      <div className="d-flex flex-column me-3">
        <Checkbox
          style={{
            width: "18px",
            height: "18px",
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
          }}
          onChange={onChange}
          name="isCounselFee"
          value={value}
        />
      </div>
      <Label>Counsel Fee</Label>
    </div>
  );
};

export const validateAddReference = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, validationAttempted, list, ...others } =
    fieldRenderProps;
  
  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      const lastPosting = list[list.length - 1];
      if (lastPosting.reference !== undefined && lastPosting.reference !== null) {
        onChange({ value: lastPosting.reference });
      }
    }
  };
  
  return (
    <div className="d-flex flex-column">
      <CLabel label="Reference" />
      <div className="d-flex flex-column">
        <Input
          style={{
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: validationAttempted && validationMessage ? "1px solid red" : undefined,
          }}
          id="reference"
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={"Add Reference"}
        ></Input>
      </div>
    </div>
  );
};

export const validateDetails = (fieldRenderProps) => {
  const { validationMessage, onChange, value, visited, validationAttempted, width, list } =
    fieldRenderProps;
  
  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      const lastPosting = list[list.length - 1];
      if (lastPosting.details !== undefined && lastPosting.details !== null) {
        onChange({ value: lastPosting.details });
      }
    }
  };
  
  return (
    <div className="d-flex flex-column">
      <CLabel label="Details" />
      <div className="d-flex flex-column">
        <TextArea
          style={{
            width: width || "327px",
            height: "60px",
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: validationAttempted && validationMessage ? "1px solid red" : undefined,
          }}
          placeholder={"Enter Details"}
          id="details"
          name="details"
          onChange={onChange}
          onKeyDown={handleKeyDown}
          type="text"
          value={value}
          rows={2}
        />
      </div>
    </div>
  );
};

export const validateNotes = (fieldRenderProps) => {
  const { validationMessage, onChange, value, visited } = fieldRenderProps;
  return (
    <div className="d-flex flex-column">
      <CLabel label="Notes" />
      <div className="d-flex flex-column">
        <TextArea
          style={{
            width: "260px",
            height: "120px",
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: visited && validationMessage ? "1px solid red" : undefined,
          }}
          placeholder={"Enter Notes"}
          id="notes"
          name="notes"
          onChange={onChange}
          type="text"
          value={value}
          rows={2}
        />
      </div>
    </div>
  );
};

export const validatePayee = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, validationAttempted, list, ...others } =
    fieldRenderProps;
  
  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      const lastPosting = list[list.length - 1];
      if (lastPosting.payee !== undefined && lastPosting.payee !== null) {
        onChange({ value: lastPosting.payee });
      }
    }
  };
  
  return (
    <div className="d-flex flex-column">
      <CLabel label="Payee" />
      <div className="d-flex flex-column">
        <Input
          style={{
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: validationAttempted && validationMessage ? "1px solid red" : undefined,
          }}
          id="payee"
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder="Add Payee"
          value={value}
        ></Input>
      </div>
    </div>
  );
};

export const validatePayer = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, validationAttempted, list, ...others } =
    fieldRenderProps;
  
  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      const lastPosting = list[list.length - 1];
      if (lastPosting.payer !== undefined && lastPosting.payer !== null) {
        onChange({ value: lastPosting.payer });
      }
    }
  };
  
  return (
    <div className="d-flex flex-column">
      <CLabel label="Payer" />
      <div className="d-flex flex-column">
        <Input
          style={{
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: validationAttempted && validationMessage ? "1px solid red" : undefined,
          }}
          id="payer"
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder="Add Payer"
          value={value}
        ></Input>
      </div>
    </div>
  );
};

export const ValidateNetAmount = (fieldRenderProps) => {
  const {
    validationMessage,
    value,
    onChange,
    visited,
    validationAttempted,
    VatCode,
    netAmountLabelSuffix,
    boxShadow = true,
    formRenderProps,
    list,
    ...others
  } = fieldRenderProps;

  const handleNetAmountChange = (e) => {
    const netAmount = e.value;
    const vatRate = VatCode?.rate || 0;
    const calculatedVat = netAmount * (vatRate / 100);

    onChange({ value: netAmount });
    formRenderProps.onChange("vatAmount", { value: calculatedVat });
  };

  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const lastPosting = list[list.length - 1];
      if (lastPosting.netAmount !== undefined && lastPosting.netAmount !== null) {
        handleNetAmountChange({ value: lastPosting.netAmount });
        blurThenRefocusForValueSync();
      }
    }
  };

  return (
    <div
      className="d-flex flex-column right-align"
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDown}
    >
      <CLabel
        label={`Net Amount${netAmountLabelSuffix ? netAmountLabelSuffix : ""}`}
      />
      <div className="d-flex flex-column">
        <NumericTextBox
          style={{
            boxShadow: boxShadow && "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: validationAttempted && validationMessage ? "1px solid red" : undefined,
          }}
          id="netAmount"
          name="netAmount"
          onChange={handleNetAmountChange}
          onKeyDown={handleKeyDown}
          value={value}
          spinners={false}
          format="n2"
          placeholder="0.00"
          {...others}
        />
      </div>
    </div>
  );
};

export const ValidateVATAmount = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, list, ...others } =
    fieldRenderProps;

  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const lastPosting = list[list.length - 1];
      if (lastPosting.vatAmount !== undefined && lastPosting.vatAmount !== null) {
        onChange({ value: lastPosting.vatAmount });
        blurThenRefocusForValueSync();
      }
    }
  };

  return (
    <div
      className="d-flex flex-column right-align"
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDown}
    >
      <CLabel label="VAT Amount" />
      <div className="d-flex flex-column">
        <NumericTextBox
          {...others}
          name="vatAmount"
          onChange={onChange}
          onKeyDown={handleKeyDown}
          style={{
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            border: visited && validationMessage ? "1px solid red" : undefined,
          }}
          placeholder="0.00"
          spinners={false}
          id="vatAmount"
          value={value} // Use provided value or calculated VAT
          format="n2"
        />
      </div>
    </div>
  );
};

export const validateDate = (fieldRenderProps) => {
  const { validationMessage, value, onChange, visited, list } = fieldRenderProps;
  const date = new Date();
  
  const handleKeyDown = (e) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const lastPosting = list[list.length - 1];
      if (lastPosting.date !== undefined && lastPosting.date !== null) {
        onChange({ value: lastPosting.date });
        blurThenRefocusForValueSync();
      }
    }
  };
  
  return (
    <div
      className="d-flex flex-column"
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDown}
    >
      <CLabel label="Date" />
      <DatePicker
        style={{ boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)" }}
        className="datepicker"
        id="date-posting"
        value={value}
        onChange={onChange}
        toggleButton={CalenderCustomToggleButton}
        format="dd/MM/yyyy"
      />
    </div>
  );
};
